import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faDatabase,
  faFileLines,
  faLayerGroup,
  faTriangleExclamation,
  faCircleCheck,
  faChevronRight,
  faChevronDown,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import {
  reportStorageService,
  formatBytes,
  type ReportStorage,
  type ReconciliationResult,
} from '@/services/report-storage.service';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/use-permissions';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from '@/lib/swal';

/**
 * B6.15 — issued Service Report storage (Owner/Admin).
 *
 * Deliberately a settings page rather than a storage-management product: it
 * answers "how much are issued documents costing" and "are they all still
 * intact", and offers no action that could change either answer. There is no
 * delete button anywhere on this screen because there is no delete endpoint
 * behind it — issued revisions are retained indefinitely (B6.15 D1/D2).
 */

function StatCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: typeof faDatabase;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function ReportRow({ report }: { report: ReportStorage }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40"
      >
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          className="h-3 w-3 shrink-0 text-muted-foreground"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">
            {report.reportNumber ?? '(no report number)'}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {report.inspectionNumber ?? '—'} · {report.revisionCount} revision
            {report.revisionCount === 1 ? '' : 's'} · {report.fileCount} file
            {report.fileCount === 1 ? '' : 's'}
          </div>
        </div>
        <div className="shrink-0 text-sm font-medium text-foreground">
          {formatBytes(report.totalSizeBytes)}
          {report.filesWithUnknownSize > 0 && (
            <span className="ml-1 text-xs font-normal text-amber-600 dark:text-amber-400">
              +{report.filesWithUnknownSize} unknown
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="space-y-3 bg-muted/20 px-4 pb-4 pt-1">
          {report.revisions.map((rev) => (
            <div key={rev.revision} className="rounded-lg border border-border bg-card p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-foreground">
                  Revision {rev.revision}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    approved {new Date(rev.approvedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-sm text-foreground">{formatBytes(rev.totalSizeBytes)}</div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-3 font-medium">File</th>
                      <th className="py-1 pr-3 font-medium">Type</th>
                      <th className="py-1 pr-3 font-medium">Size</th>
                      <th className="py-1 font-medium">Checksum (SHA-256)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rev.files.map((file) => (
                      <tr key={file.storageKey} className="border-t border-border/60">
                        <td className="py-1.5 pr-3 text-foreground">
                          {file.kind === 'BRANDING'
                            ? 'branding / logo'
                            : `photo ${file.position ?? '?'}`}
                        </td>
                        <td className="py-1.5 pr-3 text-muted-foreground">{file.mimeType}</td>
                        <td className="py-1.5 pr-3 text-foreground">
                          {file.sizeBytes === null ? (
                            <span
                              className="text-muted-foreground"
                              title="Approved before size was recorded"
                            >
                              not recorded
                            </span>
                          ) : (
                            formatBytes(file.sizeBytes)
                          )}
                        </td>
                        <td className="py-1.5 font-mono text-[11px] text-muted-foreground">
                          {file.checksumSha256.slice(0, 16)}…
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReconciliationPanel({ result }: { result: ReconciliationResult }) {
  const clean = result.issueCount === 0;
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <FontAwesomeIcon
          icon={clean ? faCircleCheck : faTriangleExclamation}
          className={`h-4 w-4 ${clean ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}
        />
        <span className="text-sm font-medium text-foreground">
          {clean
            ? `All ${result.filesVerified} file${result.filesVerified === 1 ? '' : 's'} verified`
            : `${result.issueCount} issue${result.issueCount === 1 ? '' : 's'} found`}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {result.revisionsScanned} revision{result.revisionsScanned === 1 ? '' : 's'} ·{' '}
          {result.checksumsVerified ? 'checksums verified' : 'size and existence only'}
        </span>
      </div>

      {!clean && (
        <div className="space-y-2">
          {result.findings.map((finding, i) => (
            <div
              key={`${finding.type}-${finding.storageKey}-${i}`}
              className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs"
            >
              <div className="font-medium text-foreground">
                {finding.type.replace(/_/g, ' ')}
                {finding.reportNumber && (
                  <span className="ml-2 font-normal text-muted-foreground">
                    {finding.reportNumber}
                    {finding.revision !== null ? ` rev ${finding.revision}` : ''}
                  </span>
                )}
              </div>
              <div className="mt-1 text-muted-foreground">{finding.detail}</div>
              <div className="mt-1 truncate font-mono text-[11px] text-muted-foreground">
                {finding.storageKey}
              </div>
            </div>
          ))}
          <p className="flex items-start gap-2 pt-1 text-xs text-muted-foreground">
            <FontAwesomeIcon icon={faShieldHalved} className="mt-0.5 h-3 w-3 shrink-0" />
            Nothing was deleted or modified. Issued documents are retained indefinitely and are
            never removed automatically — these findings are for investigation.
          </p>
        </div>
      )}
    </div>
  );
}

export function ReportStoragePage() {
  const { has } = usePermissions();
  const canView = has('inspection:report-storage:view');
  const [result, setResult] = useState<ReconciliationResult | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report-storage-summary'],
    queryFn: () => reportStorageService.getSummary(),
    enabled: canView,
  });

  const reconcile = useMutation({
    mutationFn: (verifyChecksums: boolean) => reportStorageService.reconcile(verifyChecksums),
    onSuccess: (r) => {
      setResult(r);
      if (r.issueCount === 0) toast.success('Reconciliation complete — no issues found');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Reconciliation failed')),
  });

  if (!canView) {
    return (
      <p className="rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
        You do not have permission to view report storage.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
        {getApiErrorMessage(error, 'Could not load report storage.')}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Report Storage</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Storage used by issued Service Report documents. Approved documents are kept indefinitely
          and are never deleted automatically.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={faDatabase}
          label="Total"
          value={formatBytes(data.totalSizeBytes)}
          hint={
            data.filesWithUnknownSize > 0
              ? `+${data.filesWithUnknownSize} file(s) with no recorded size`
              : undefined
          }
        />
        <StatCard icon={faFileLines} label="Reports" value={String(data.reportCount)} />
        <StatCard
          icon={faLayerGroup}
          label="Revisions"
          value={String(data.revisionCount)}
          hint={`${data.fileCount} stored file${data.fileCount === 1 ? '' : 's'}`}
        />
        <StatCard
          icon={faLayerGroup}
          label="Largest revision"
          value={data.largestRevision ? formatBytes(data.largestRevision.sizeBytes) : '—'}
          hint={
            data.largestRevision
              ? `${data.largestRevision.reportNumber ?? 'report'} rev ${data.largestRevision.revision}`
              : undefined
          }
        />
      </div>

      {/* Reconciliation */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => reconcile.mutate(false)}
            disabled={reconcile.isPending}
          >
            {reconcile.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faShieldHalved} className="mr-2 h-3.5 w-3.5" />
            )}
            Check storage
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => reconcile.mutate(true)}
            disabled={reconcile.isPending}
          >
            Verify checksums
          </Button>
          <span className="text-xs text-muted-foreground">
            Read-only. Checksum verification reads every file.
          </span>
        </div>

        {result && <ReconciliationPanel result={result} />}
      </div>

      {/* Per-report drill-down */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          By report
        </div>
        {data.reports.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            No reports have issued a document yet.
          </p>
        ) : (
          data.reports.map((report) => <ReportRow key={report.reportId} report={report} />)
        )}
      </div>
    </div>
  );
}
